import React, { useMemo, useState } from "react";
import { useEditor } from "./store";
import { secondsToPx, pxToSeconds } from "./helpers";
import { FiMessageCircle } from "react-icons/fi";
import { FaMapMarker } from "react-icons/fa";


const TICK_STEPS_SECONDS = [
  0.05, 0.1, 0.2, 0.5,
  1, 2, 5, 10, 15, 30,
  60, 120, 300, 600
];

function getMajorTickStep(zoom) {
  const minLabelSpacingPx = 80;
  const requiredStep = minLabelSpacingPx / zoom;
  return TICK_STEPS_SECONDS.find((step) => step >= requiredStep) ?? TICK_STEPS_SECONDS[TICK_STEPS_SECONDS.length - 1];
}

function getMinorTickStep(majorStep) {
  const subdivisions = [10, 5, 4, 2];
  for (const divisor of subdivisions) {
    const candidate = majorStep / divisor;
    if (Number.isFinite(candidate) && candidate >= 0.05) return candidate;
  }
  return majorStep;
}

function formatRulerTime(seconds, tickStep) {
  if (seconds >= 60) {
    const whole = Math.round(seconds);
    const mins = Math.floor(whole / 60);
    const secs = whole % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }

  if (tickStep < 0.1) return `${seconds.toFixed(2)}s`;
  if (tickStep < 1) return `${seconds.toFixed(1)}s`;
  return `${Math.round(seconds)}s`;
}

export default function TimeRuler({ timelineEnd, zoom, onScroll, rulerContentRef, comments = [], onCommentMarkerClick }) {
  const playhead = useEditor((s) => s.playhead);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const setPlaying = useEditor((s) => s.setPlaying);
  const [isDragging, setIsDragging] = useState(false);

  const contentWidthPx = secondsToPx(timelineEnd, zoom);
  const rulerHeight = 28;
  const commentMarkers = useMemo(() => comments.filter(c => c.timestamp != null && !c.resolved), [comments]);
  const majorTickStep = useMemo(() => getMajorTickStep(zoom), [zoom]);
  const minorTickStep = useMemo(() => getMinorTickStep(majorTickStep), [majorTickStep]);
  const majorTicks = useMemo(() => {
    const count = Math.ceil(timelineEnd / majorTickStep) + 1;
    return Array.from({ length: count }, (_, i) => i * majorTickStep);
  }, [timelineEnd, majorTickStep]);
  const minorTicks = useMemo(() => {
    if (minorTickStep >= majorTickStep) return [];
    const count = Math.ceil(timelineEnd / minorTickStep) + 1;
    const epsilonSeconds = minorTickStep / 20;
    return Array.from({ length: count }, (_, i) => i * minorTickStep)
      .filter((time) => {
        const nearestMajor = Math.round(time / majorTickStep) * majorTickStep;
        return Math.abs(time - nearestMajor) > epsilonSeconds;
      });
  }, [timelineEnd, majorTickStep, minorTickStep]);

  function getTimeFromEvent(e) {
    const rect = rulerContentRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left;
    const scrollX = rulerContentRef.current.scrollLeft;
    const timelineX = pxX + scrollX;
    return Math.max(0, pxToSeconds(timelineX, zoom));
  }

  function onPointerDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    setPlaying(false);
    setPlayhead(getTimeFromEvent(e));
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e) {
    if (!isDragging) return;
    e.preventDefault();
    if (rulerContentRef.current) {
      const el = rulerContentRef.current;
      const rect = el.getBoundingClientRect();
      const pointerX = e.clientX - rect.left;
      const followOffsetPx = 100;
      const maxStepPx = 12;
      const edgeSpeedFactor = 0.25;
      const maxScroll = Math.max(0, contentWidthPx - el.clientWidth);
      let nextScroll = el.scrollLeft;

      if (pointerX > el.clientWidth - followOffsetPx) {
        const overflow = pointerX - (el.clientWidth - followOffsetPx);
        const push = Math.min(maxStepPx, overflow * edgeSpeedFactor);
        nextScroll = Math.min(maxScroll, el.scrollLeft + push);
      } else if (pointerX < followOffsetPx) {
        const overflow = followOffsetPx - pointerX;
        const push = Math.min(maxStepPx, overflow * edgeSpeedFactor);
        nextScroll = Math.max(0, el.scrollLeft - push);
      }

      if (nextScroll !== el.scrollLeft) {
        el.scrollLeft = nextScroll;
        onScroll(nextScroll);
      }
    }
    setPlayhead(getTimeFromEvent(e));
  }

  function onPointerUp() {
    setIsDragging(false);
  }

  function handleScroll(e) {
    if (isDragging) return;
    onScroll(e.target.scrollLeft);
  }

  return (
    <div className="flex border-b bg-input" style={{ height: rulerHeight }}>
      {/* Fixed label */}
      <div className="w-40 min-w-[160px] bg-background  border-r flex items-center px-2">
        <span className="text-[10px] text-secondary-foreground/80 uppercase select-none">Timeline</span>
      </div>

      {/* Scrollable ruler content - hidden scrollbar */}
      <div
        ref={rulerContentRef}
        className="flex-1 overflow-x-auto overflow-y-hidden cursor-pointer select-none scrollbar-hide"
        style={{ touchAction: "none", overscrollBehaviorX: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onScroll={handleScroll}
      >
        <div style={{ width: contentWidthPx, height: rulerHeight }} className="relative">
          {/* Minor markers */}
          {minorTicks.map((time) => (
            <div
              key={`minor-${time}`}
              className="absolute bottom-0 border-l border-zinc-700/40"
              style={{ left: secondsToPx(time, zoom), height: 8, width: 1 }}
            />
          ))}

          {/* Major markers + labels */}
          {majorTicks.map((time) => (
            <div
              key={`major-${time}`}
              className="absolute top-0 bottom-0 border-l border-zinc-700/70"
              style={{ left: secondsToPx(time, zoom), width: 1 }}
            >
              <div className="absolute top-0 left-1 text-[10px] text-zinc-400 font-medium whitespace-nowrap">
                {formatRulerTime(time, majorTickStep)}
              </div>
            </div>
          ))}

          {/* Playhead marker */}
          <div
            className="absolute top-0 bottom-0 w-[2px] bg-primary z-10"
            style={{ left: secondsToPx(playhead, zoom) }}
          >
            {/* Playhead handle */}
            {/* <div 
              className="absolute -top-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary rounded-sm"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% 60%, 50% 100%, 0 60%)' }}
            /> */}
          </div>

          {/* Comment markers */}
          {commentMarkers.map((comment) => (
            <div
              key={comment._id}
              className="absolute top-0 z-20 flex items-end justify-center cursor-pointer group pb-0.5"
              style={{ left: secondsToPx(comment.timestamp, zoom) - 8, width: 16, height: rulerHeight }}
              onClick={(e) => {
                e.stopPropagation();
                onCommentMarkerClick?.(comment._id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              title={comment.text}
            >
              <FaMapMarker className="h-4 text-secondary-foreground/80 group-hover:text-secondary-foreground " />
              {/* <FiMessageCircle className="w-2.5 h-2.5 text-amber-400 group-hover:text-amber-300 drop-shadow-sm" /> */}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
